import { startLobbyConnection } from '@bn-games/social-client';
import type { LobbyConnection, PresenceStatus } from '@bn-games/social-client';

const DEFAULT_LOBBY_URL = 'ws://localhost:3001/ws/lobby';

/** Same pattern every game app uses for its own WS URL — see e.g.
 * `apps/backgammon/src/main.ts`'s `wsServerUrl` — landing just has its own
 * default suffix. `scripts/build-site.mjs` supplies the real value at build
 * time via the same `VITE_WS_SERVER_URL` var every app already reads. */
export function lobbyServerUrl(): string {
  return (import.meta.env['VITE_WS_SERVER_URL'] as string | undefined) ?? DEFAULT_LOBBY_URL;
}

/**
 * The friends page's view of the page-lifetime lobby connection: it keeps one
 * `watch()` registration in sync with the current friends list and caches the
 * answers. The connection itself is shared with the invite dock, so this never
 * opens or closes the socket — `dispose()` only withdraws this page's watch and
 * listener. Kept in its own file so `FriendsController.ts` stays page wiring.
 */
export class FriendsPresence {
  private readonly connection: LobbyConnection;
  private readonly statuses = new Map<string, PresenceStatus>();
  private readonly listeners = new Set<() => void>();
  private readonly stopPresence: () => void;
  private unwatch: (() => void) | null = null;

  constructor(serverUrl: string) {
    this.connection = startLobbyConnection({ serverUrl });
    this.stopPresence = this.connection.onPresence((incoming) => {
      for (const status of incoming) {
        this.statuses.set(status.userId, status);
      }
      for (const listener of this.listeners) listener();
    });
  }

  /** Replaces the full set of ids to keep live presence for — call again with
   * the updated friend list any time it changes (add/remove). */
  watch(userIds: readonly string[]): void {
    const previous = this.unwatch;
    // Register the new list before dropping the old one, so the connection's
    // watch union never passes through empty and toggles its hello off and on.
    this.unwatch = this.connection.watch(userIds);
    if (previous !== null) previous();
  }

  /** `undefined` means "no answer yet" (including a friend whose consent
   * setting never authorizes this viewer) — callers render that as offline,
   * same as an explicit `offline` entry with no last-seen data. */
  status(userId: string): PresenceStatus | undefined {
    return this.statuses.get(userId);
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  dispose(): void {
    if (this.unwatch !== null) this.unwatch();
    this.unwatch = null;
    this.stopPresence();
    this.listeners.clear();
  }
}
